/**
 * @file src/App.tsx
 * @exports App
 * @summary Root route component wiring global providers and theme (formerly `src/pages/_app.tsx`).
 * @remarks Wraps every route with React Query, PostHog (the client set up in src/helpers/analytics), Jotai
 *          atoms, styled-components theme, analytics (including `Screen Viewed` via useScreenAnalytics and ID
 *          capture reloads via useIdCaptureReloadAnalytics), Sentry error tracking, Mantine
 *          notifications, global CSS, window-size helpers, and high-contrast theme swap for vID and
 *          authentication pages. Routed pages render through the TanStack Router `<Outlet />`; errors
 *          that escape the router render `AppErrorFallback` rather than an empty page. Imports
 *          `src/manager` so GraphQL outages navigate to `paths.vidServiceUnavailable` via Manager rather
 *          than replacing the outlet.
 */

import {
	GlobalStyle,
	ThemeProvider,
	cerebellumTheme,
	createCustomMantineTheme,
	highContrastTheme,
} from "@cerebruminc/cerebellum";
import { MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Outlet, useLocation } from "@tanstack/react-router";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { PostHogProvider } from "posthog-js/react";
import React, { useEffect, useMemo, useRef } from "react";
import { HelmetProvider } from "react-helmet-async";
import { AppErrorFallback } from "src/components/AppErrorFallback/AppErrorFallback";
import { AUTH_PAGE_PATHS, paths } from "src/const";
import { env } from "src/environment";
import { analytics, ANALYTICS } from "src/helpers";
import { useRouteHistory } from "src/hooks/useRouteHistory";
import { useScreenAnalytics } from "src/hooks/useScreenAnalytics";
import { useIdCaptureReloadAnalytics } from "src/hooks/vid/useIdCaptureReloadAnalytics";
import { useWindowDimensions } from "src/hooks/useWindowDimensions";
// Ensure the Manager singleton registers GraphQL outage handling before any requests.
import "src/manager";
import { bannerShownAtom, highContrastThemeAtom, modalOpenAtom, windowSizeAtom } from "src/shame/shame-atoms";
import { CAPTURE_MODAL_Z_INDEX } from "src/views/VID/components/CameraView/styles";
import { VidSessionKeyGuard } from "src/views/VID/views/VidSessionKeyGuard";
import packageJSON from "./../package.json";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			staleTime: 5 * 1000 * 60,
		},
	},
});

const printBanner = () => {
	console.log(`

   __________  _______   ____________________  _   __
  / ____/ __ \\/ ____/ | / /  _/_  __/  _/ __ \\/ | / /
 / /   / / / / / __/  |/ // /  / /  / // / / /  |/ /
/ /___/ /_/ / /_/ / /|  // /  / / _/ // /_/ / /|  /
\\____/\\____/\\____/_/ |_/___/ /_/ /___/\\____/_/ |_/

       Welcome to cognition v${packageJSON.version}
  `);
};

/** Loads the Google Maps Places script once at startup (formerly a `next/script` tag in `_app`). */
const useGoogleMapsScript = () => {
	useEffect(() => {
		const src = `https://maps.googleapis.com/maps/api/js?key=${env.GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
		if (document.querySelector(`script[src="${src}"]`)) {
			return;
		}
		const script = document.createElement("script");
		script.src = src;
		script.async = true;
		document.head.appendChild(script);
	}, []);
};

/** What Sentry's boundary shows for an error the router did not catch; without it the boundary renders nothing. */
const renderErrorFallback = ({ error, resetError }: { error: unknown; resetError: () => void }) => (
	<AppErrorFallback error={error} reset={resetError} />
);

export const App = () => {
	const location = useLocation();
	const [bannerShown, setBannerShown] = useAtom(bannerShownAtom);

	useScreenAnalytics();
	useIdCaptureReloadAnalytics();
	useGoogleMapsScript();

	// Translation detection for analytics. Finding out when users have
	// translated the website will give us insight on which langs to support
	const initialLangRef = useRef<string>("");

	useEffect(() => {
		if (typeof document !== "undefined") {
			initialLangRef.current = document.documentElement.lang;
			const detectTranslation = () => {
				const currentLang = document.documentElement.lang;
				if (currentLang !== initialLangRef.current) {
					analytics.track(ANALYTICS.websiteTranslated, { lang: currentLang });
					initialLangRef.current = currentLang;
				}
			};
			// Setup MutationObserver to watch for lang attribute changes
			const observer = new MutationObserver(() => {
				detectTranslation();
			});
			// Observe changes on <html> for lang attribute
			observer.observe(document.documentElement, {
				attributes: true,
				attributeFilter: ["lang"], // Only watch for 'lang' attribute changes
			});
			return () => {
				observer.disconnect(); // Cleanup observer when component is unmounted
			};
		}
	}, []);

	// Swap to high-contrast cerebellum theme on vID and auth pages when enabled
	const isOnVidPage = location.pathname.startsWith("/vid/") || location.pathname === paths.vidRemoteIdUpload;
	const isOnAuthPage = AUTH_PAGE_PATHS.has(location.pathname) || location.pathname.startsWith("/auth/");
	const isHighContrast = useAtomValue(highContrastThemeAtom);
	const isHighContrastActive = isHighContrast && (isOnVidPage || isOnAuthPage);
	const activeTheme = isHighContrastActive ? highContrastTheme : cerebellumTheme;
	const mantineTheme = useMemo(
		() => createCustomMantineTheme({ highContrast: isHighContrastActive }),
		[isHighContrastActive],
	);

	// Lock scrolling when a modal is open
	const isModalOpen = useAtomValue(modalOpenAtom);
	useEffect(() => {
		document.body.className = isModalOpen ? "neverScroll" : "";
	}, [isModalOpen]);

	// Only print the banner once. We have to use a state variable because useEffect fails due to withLDProvider
	// forcibly rerendering `MyApp` multiple times on first load.
	useEffect(() => {
		if (!bannerShown) {
			printBanner();
			setBannerShown(true);
		}
	}, [bannerShown, setBannerShown]);

	// Track window size for the whole app
	const { height, width } = useWindowDimensions();
	const setWindowSize = useSetAtom(windowSizeAtom);
	useEffect(() => {
		setWindowSize({ height, width });
	}, [height, setWindowSize, width]);

	// Initialize route history tracking at app level
	useRouteHistory();

	return (
		<HelmetProvider>
			<Sentry.ErrorBoundary fallback={renderErrorFallback} showDialog>
				<PostHogProvider client={analytics.posthog}>
					<ThemeProvider theme={activeTheme}>
						<MantineProvider theme={mantineTheme}>
							<QueryClientProvider client={queryClient}>
								<ModalsProvider>
									<GlobalStyle />
									<Notifications position="top-center" zIndex={CAPTURE_MODAL_Z_INDEX} />
									<ReactQueryDevtools initialIsOpen={false} />
									<VidSessionKeyGuard>
										<Outlet />
									</VidSessionKeyGuard>
								</ModalsProvider>
							</QueryClientProvider>
						</MantineProvider>
					</ThemeProvider>
				</PostHogProvider>
			</Sentry.ErrorBoundary>
		</HelmetProvider>
	);
};
